import { useGetManyReference } from "react-admin";
import { useWatch } from "react-hook-form";
import { DateTime, Interval } from "luxon";
import { Student } from "../../providers/studentsProvider";
import { type Simulator } from "../../providers/vehiclesProvider";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { WeekdayEnum } from "../../model/Weekday";
import { timeOfDayToDateTime } from "../../model/TimeOfDay";
import { DrivingLesson } from "../../model/autovioCalendarEvents";
import { useCurrentDrivingSchoolId } from "../../hooks/useCurrentDrivingSchoolId";

export function useSimulatorOptions(fixedSimulatorId?: string): {
  simulatorOptions: undefined | Array<SimulatorOption>;
  getSettledSimulatorOptions: () => Promise<Array<SimulatorOption>>;
} {
  const [availabilityMap, setAvailabilityMap] = useState<Map<string, SimulatorAvailabilityStatus>>(new Map());
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const branchId = (useWatch({ name: "student" }) as undefined | Student)?.branchId;
  const dateTime = useWatch({ name: "dateTime" }) as undefined | DateTime;
  const durationInMinutes = useWatch({ name: "durationInMinutes" }) as undefined | number;
  const { data: simulators } = useGetManyReference<Simulator>("simulators", {
    target: "drivingSchoolId",
    id: drivingSchoolId,
    filter: !fixedSimulatorId && branchId ? { branchId } : {},
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 999 },
  });
  const availabilityCacheRef = useRef<Map<string, Map<string, SimulatorAvailabilityStatus>>>(new Map()).current;
  const calendarEventsPromiseMap = useRef<Map<string, Promise<Array<DrivingLesson>>>>(new Map()).current;

  const buildAvailabilityMap = useCallback(async (): Promise<Map<string, SimulatorAvailabilityStatus>> => {
    const newAvailabilityMap = new Map<string, SimulatorAvailabilityStatus>();
    const availabilityCacheKey = _buildAvailabilityCacheKey(simulators, drivingSchoolId, dateTime, durationInMinutes);
    if (!(availabilityCacheKey && simulators && drivingSchoolId && dateTime?.isValid && durationInMinutes)) {
      return newAvailabilityMap;
    }
    const cachedAvailabilityMap = availabilityCacheRef.get(availabilityCacheKey);
    if (cachedAvailabilityMap) {
      return cachedAvailabilityMap;
    }

    for (const simulator of simulators) {
      const availabilityMapKey = `${simulator.id}@${dateTime.toMillis()}+${durationInMinutes}`;
      const timeRange = Interval.fromDateTimes(dateTime, dateTime.plus({ minutes: durationInMinutes }));
      const isWithinAvailability = _isAvailable(simulator, timeRange);
      const drivingLessons = await getDrivingLessons_(
        simulator,
        _weekOf(dateTime),
        drivingSchoolId,
        calendarEventsPromiseMap,
      );
      const hasTimeConflict = _findTimeConflict(drivingLessons, timeRange);
      newAvailabilityMap.set(availabilityMapKey, { isWithinAvailability, hasTimeConflict });
    }
    availabilityCacheRef.set(availabilityCacheKey, newAvailabilityMap);
    return newAvailabilityMap;
  }, [simulators, drivingSchoolId, dateTime, durationInMinutes]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const availabilityMap = await buildAvailabilityMap();
      if (!cancelled) {
        setAvailabilityMap(availabilityMap);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buildAvailabilityMap]);

  const buildSimulatorOptions = useCallback(
    (availabilityMapOverride?: Map<string, SimulatorAvailabilityStatus>): Array<SimulatorOption> | undefined => {
      if (!simulators) {
        return undefined;
      }
      const finalAvailabilityMap = availabilityMapOverride ?? availabilityMap;
      const options: Array<SimulatorOption> = [];
      for (const simulator of simulators) {
        let option: SimulatorOption;
        if (!(dateTime && durationInMinutes)) {
          option = [simulator.id, simulator.name];
        } else {
          const availability = finalAvailabilityMap.get(`${simulator.id}@${dateTime.toMillis()}+${durationInMinutes}`);
          if (!availability) {
            option = [simulator.id, `${simulator.name} (überprüfe Verfügbarkeit ...)`];
          } else if (availability.hasTimeConflict) {
            option = [simulator.id, `${simulator.name} (nicht verfügbar)`];
            option.hasTimeConflict = availability.hasTimeConflict;
            option.isWithinAvailability = availability.isWithinAvailability;
          } else if (availability.isWithinAvailability) {
            option = [simulator.id, `${simulator.name} (verfügbar)`] as SimulatorOption;
            option.available = Interval.fromDateTimes(dateTime, dateTime.plus({ minutes: durationInMinutes }));
            option.hasTimeConflict = availability.hasTimeConflict;
            option.isWithinAvailability = availability.isWithinAvailability;
          } else {
            option = [simulator.id, `${simulator.name} (außerhalb Verfügbarkeit)`] as SimulatorOption;
            option.available = Interval.fromDateTimes(dateTime, dateTime.plus({ minutes: durationInMinutes }));
            option.hasTimeConflict = availability.hasTimeConflict;
            option.isWithinAvailability = availability.isWithinAvailability;
          }
        }
        option.simulatorName = simulator.name;
        option.branchId = simulator.branchId;
        options.push(option);
      }
      return options;
    },
    [simulators, dateTime, durationInMinutes, availabilityMap],
  );

  const simulatorOptions = useMemo(() => buildSimulatorOptions(), [buildSimulatorOptions]);

  const getSettledSimulatorOptions = useCallback(async (): Promise<Array<SimulatorOption>> => {
    const settledAvailabilityMap = await buildAvailabilityMap();
    const options = buildSimulatorOptions(settledAvailabilityMap);
    return options ?? [];
  }, [buildSimulatorOptions, buildAvailabilityMap]);

  return { simulatorOptions, getSettledSimulatorOptions };
}

function getDrivingLessons_(
  simulator: Simulator,
  week: { start: DateTime; end: DateTime },
  drivingSchoolId: string,
  calendarEventsPromiseMap: Map<string, Promise<Array<DrivingLesson>>>,
): Promise<Array<DrivingLesson>> {
  const cacheKey = `${simulator.id}@${week.start}`;
  let promise = calendarEventsPromiseMap.get(cacheKey);
  if (!promise) {
    promise = getDrivingLessons__(simulator, week, drivingSchoolId);
    calendarEventsPromiseMap.set(cacheKey, promise);
  }
  return promise;
}

async function getDrivingLessons__(
  simulator: Simulator,
  week: { start: DateTime; end: DateTime },
  drivingSchoolId: string,
) {
  const result = await dataProvider.getList("calendarEvents", {
    filter: {
      drivingSchoolId,
      vehicleIds: simulator.id,
      dateRange: Interval.fromDateTimes(week.start, week.end),
    },
    pagination: { page: 1, perPage: 9999 },
    sort: { field: "start", order: "ASC" },
  });
  return result.data as Array<DrivingLesson>;
}

function _isAvailable(simulator: Simulator, timeRange: Interval) {
  const weekday = WeekdayEnum.options[timeRange.start.weekday - 1];
  for (const [_, from, to] of simulator.availabilities.filter((it) => it[0] === weekday)) {
    const availabilityFrom = timeOfDayToDateTime(from, timeRange.start);
    if (availabilityFrom <= timeRange.start) {
      const availabilityTo = timeOfDayToDateTime(to, timeRange.end);
      if (timeRange.end <= availabilityTo) {
        return true;
      }
    }
  }
  return false;
}

function _weekOf(dateTime: DateTime): { start: DateTime; end: DateTime } {
  return { start: dateTime.startOf("week"), end: dateTime.endOf("week") };
}

function _findTimeConflict(drivingLessons: Array<DrivingLesson>, timeRange: Interval) {
  return drivingLessons.some((drivingLesson) => _isTimeConflict(drivingLesson, timeRange));
}

function _isTimeConflict(drivingLesson: DrivingLesson, timeRange: Interval) {
  return timeRange.overlaps(Interval.fromDateTimes(drivingLesson.start, drivingLesson.end));
}

const _buildAvailabilityCacheKey = (
  simulators?: Array<Simulator>,
  drivingSchoolId?: string,
  dateTime?: DateTime,
  durationInMinutes?: number,
): string | undefined => {
  if (!(simulators?.length && drivingSchoolId && dateTime?.isValid && durationInMinutes)) {
    return undefined;
  }
  const simulatorIds = simulators
    .map((simulator) => simulator.id)
    .sort()
    .join(",");
  return [drivingSchoolId, String(dateTime.toMillis()), String(durationInMinutes), simulatorIds].join("|");
};

export type SimulatorOption = [/* id: */ string, /* label: */ string] & {
  simulatorName?: string;
  available?: Interval;
  hasTimeConflict?: boolean;
  isWithinAvailability?: boolean;
  branchId?: string;
};

interface SimulatorAvailabilityStatus {
  isWithinAvailability: boolean;
  hasTimeConflict: boolean;
}
